import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiSend, FiSearch, FiMail, FiCode, FiCheck, FiX, FiAlertCircle } from 'react-icons/fi';
import './EmailTemplates.css';
import { Button } from '../../components/Button';
import { API_URL } from '../../api/client';

interface EmailTemplate {
  template_id: string;
  template_name: string;
  template_key: string;
  subject: string;
  body_html: string;
  body_text: string;
  variables: string[];
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ToastMessage {
  id: number;
  type: 'success' | 'error' | 'info';
  message: string;
}

const EmailTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [testEmail, setTestEmail] = useState('');
  const [sampleVariables, setSampleVariables] = useState<any>({});
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    template_name: '',
    template_key: '',
    subject: '',
    body_html: '',
    body_text: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    fetchTemplates();
  }, [isActiveFilter, searchQuery]);

  const showToast = (type: 'success' | 'error' | 'info', message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (isActiveFilter !== 'all') params.append('is_active', isActiveFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(
        `${API_URL}/admin/email-templates?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        setTemplates(result.data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingTemplate
        ? `${API_URL}/admin/email-templates/${editingTemplate.template_id}`
        : `${API_URL}/admin/email-templates`;

      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (result.success) {
        showToast('success', `Template ${editingTemplate ? 'updated' : 'created'} successfully!`);
        setShowModal(false);
        resetForm();
        fetchTemplates();
      } else {
        showToast('error', result.message || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      showToast('error', 'An error occurred while saving the template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/admin/email-templates/${templateId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        showToast('success', 'Template deleted successfully!');
        fetchTemplates();
      } else {
        showToast('error', result.message || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      showToast('error', 'An error occurred while deleting the template');
    }
  };

  const handlePreview = async (template: EmailTemplate) => {
    setEditingTemplate(template);

    // Create sample variables object
    const sampleVars: any = {};
    template.variables.forEach((variable: string) => {
      sampleVars[variable] = `[${variable}]`;
    });
    setSampleVariables(sampleVars);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/admin/email-templates/${template.template_id}/preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ variables: sampleVars }),
        }
      );

      const result = await response.json();
      if (result.success) {
        setPreviewData(result.data);
        setShowPreviewModal(true);
      } else {
        showToast('error', result.message || 'Failed to preview template');
      }
    } catch (error) {
      console.error('Error previewing template:', error);
      showToast('error', 'An error occurred while previewing the template');
    }
  };

  const handleTestSend = async () => {
    if (!testEmail || !editingTemplate) {
      showToast('error', 'Please enter a test email address');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/admin/email-templates/${editingTemplate.template_id}/test`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            test_email: testEmail,
            variables: sampleVariables,
          }),
        }
      );

      const result = await response.json();
      if (result.success) {
        showToast('success', 'Test email sent successfully!');
        setShowTestModal(false);
        setTestEmail('');
      } else {
        showToast('error', result.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      showToast('error', 'An error occurred while sending test email');
    }
  };

  const resetForm = () => {
    setFormData({
      template_name: '',
      template_key: '',
      subject: '',
      body_html: '',
      body_text: '',
      description: '',
      is_active: true,
    });
    setEditingTemplate(null);
  };

  const openEditModal = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      template_name: template.template_name,
      template_key: template.template_key,
      subject: template.subject,
      body_html: template.body_html,
      body_text: template.body_text || '',
      description: template.description || '',
      is_active: template.is_active,
    });
    setShowModal(true);
  };

  const filteredTemplates = templates;
  const activeCount = templates.filter(t => t.is_active).length;
  const inactiveCount = templates.filter(t => !t.is_active).length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-templates-page" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)', padding: '2rem' }}>
      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2" style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 50 }}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast-notification flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg transform transition-all duration-300 ${
              toast.type === 'success'
                ? 'bg-green-500 text-white'
                : toast.type === 'error'
                ? 'bg-red-500 text-white'
                : 'bg-blue-500 text-white'
            }`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              backgroundColor: toast.type === 'success' ? '#10b981' : toast.type === 'error' ? '#ef4444' : '#3b82f6',
              color: 'white'
            }}
          >
            {toast.type === 'success' && <FiCheck size={20} />}
            {toast.type === 'error' && <FiX size={20} />}
            {toast.type === 'info' && <FiAlertCircle size={20} />}
            <span style={{ fontWeight: 500 }}>{toast.message}</span>
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '2.25rem', fontWeight: 'bold', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <FiMail style={{ color: '#3b82f6' }} size={36} />
              Email Templates
            </h1>
            <p style={{ color: '#6b7280', marginTop: '0.5rem', fontSize: '1rem' }}>
              Manage automated email templates for your platform
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            variant="primary"
            size="md"
          >
            <FiPlus size={20} /> Create New Template
          </Button>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div className="stat-card" style={{
            background: 'white',
            borderRadius: '1rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            padding: '1.5rem',
            borderLeft: '4px solid #3b82f6'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Total Templates
                </p>
                <p style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#1f2937', marginTop: '0.25rem' }}>
                  {templates.length}
                </p>
              </div>
              <div style={{ background: '#dbeafe', padding: '0.75rem', borderRadius: '9999px' }}>
                <FiMail size={24} style={{ color: '#3b82f6' }} />
              </div>
            </div>
          </div>

          <div className="stat-card" style={{
            background: 'white',
            borderRadius: '1rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            padding: '1.5rem',
            borderLeft: '4px solid #10b981'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Active Templates
                </p>
                <p style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#1f2937', marginTop: '0.25rem' }}>
                  {activeCount}
                </p>
              </div>
              <div style={{ background: '#d1fae5', padding: '0.75rem', borderRadius: '9999px' }}>
                <FiCheck size={24} style={{ color: '#10b981' }} />
              </div>
            </div>
          </div>

          <div className="stat-card" style={{
            background: 'white',
            borderRadius: '1rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            padding: '1.5rem',
            borderLeft: '4px solid #6b7280'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Inactive Templates
                </p>
                <p style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#1f2937', marginTop: '0.25rem' }}>
                  {inactiveCount}
                </p>
              </div>
              <div style={{ background: '#f3f4f6', padding: '0.75rem', borderRadius: '9999px' }}>
                <FiX size={24} style={{ color: '#6b7280' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <FiSearch style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} size={20} />
            <input
              type="text"
              placeholder="Search templates by name, key, or subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '3rem',
                paddingRight: '1rem',
                paddingTop: '0.875rem',
                paddingBottom: '0.875rem',
                border: '2px solid #e5e7eb',
                borderRadius: '0.75rem',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>
          <select
            value={isActiveFilter}
            onChange={(e) => setIsActiveFilter(e.target.value)}
            style={{
              padding: '0.875rem 1rem',
              border: '2px solid #e5e7eb',
              borderRadius: '0.75rem',
              background: 'white',
              fontWeight: 500,
              fontSize: '1rem',
              cursor: 'pointer',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          >
            <option value="all">All Status</option>
            <option value="true">Active Only</option>
            <option value="false">Inactive Only</option>
          </select>
        </div>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '1rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          padding: '3rem',
          textAlign: 'center'
        }}>
          <FiMail size={64} style={{ margin: '0 auto 1rem', color: '#d1d5db' }} />
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
            No Templates Found
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            {searchQuery || isActiveFilter !== 'all'
              ? 'Try adjusting your filters to see more results.'
              : 'Get started by creating your first email template.'}
          </p>
          {!searchQuery && isActiveFilter === 'all' && (
            <Button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              variant="primary"
              size="md"
            >
              <FiPlus /> Create Your First Template
            </Button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '1.5rem' }}>
          {filteredTemplates.map((template) => (
            <div
              key={template.template_id}
              style={{
                background: 'white',
                borderRadius: '1rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                border: '1px solid #f3f4f6'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Card Header */}
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                padding: '1.25rem',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <div style={{ background: 'rgba(255, 255, 255, 0.2)', padding: '0.5rem', borderRadius: '0.5rem' }}>
                      <FiMail size={20} style={{ color: 'white' }} />
                    </div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'white', margin: 0 }}>
                      {template.template_name}
                    </h3>
                  </div>
                  <code style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '0.375rem',
                    fontSize: '0.75rem',
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontFamily: 'monospace'
                  }}>
                    {template.template_key}
                  </code>
                </div>
                <span style={{
                  background: template.is_active ? 'rgba(16, 185, 129, 0.2)' : 'rgba(156, 163, 175, 0.2)',
                  color: template.is_active ? '#10b981' : '#9ca3af',
                  padding: '0.375rem 0.875rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  border: `2px solid ${template.is_active ? 'rgba(16, 185, 129, 0.3)' : 'rgba(156, 163, 175, 0.3)'}`
                }}>
                  {template.is_active ? <FiCheck size={12} /> : <FiX size={12} />}
                  {template.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Card Body */}
              <div style={{ padding: '1.25rem' }}>
                {/* Description */}
                <p style={{
                  color: '#6b7280',
                  fontSize: '0.875rem',
                  marginBottom: '1rem',
                  minHeight: '2.5rem',
                  lineHeight: '1.5'
                }}>
                  {template.description || 'No description provided'}
                </p>

                {/* Subject */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Subject Line
                  </label>
                  <p style={{
                    color: '#374151',
                    fontSize: '0.875rem',
                    padding: '0.625rem 0.875rem',
                    background: '#f9fafb',
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb',
                    fontWeight: 500
                  }}>
                    {template.subject}
                  </p>
                </div>

                {/* Variables */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Template Variables ({template.variables.length})
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {template.variables.length > 0 ? (
                      template.variables.slice(0, 4).map((variable, idx) => (
                        <span
                          key={idx}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.375rem',
                            padding: '0.375rem 0.75rem',
                            background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                            color: '#7c3aed',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: '0.375rem',
                            border: '1px solid #e9d5ff'
                          }}
                        >
                          <FiCode size={12} />
                          {variable}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '0.875rem', fontStyle: 'italic' }}>No variables</span>
                    )}
                    {template.variables.length > 4 && (
                      <span style={{
                        padding: '0.375rem 0.75rem',
                        background: '#f3f4f6',
                        color: '#6b7280',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        borderRadius: '0.375rem'
                      }}>
                        +{template.variables.length - 4} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '0.5rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #f3f4f6'
                }}>
                  <Button
                    onClick={() => handlePreview(template)}
                    variant="secondary"
                    size="sm"
                    title="Preview Template"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <FiEye size={18} />
                    <span>Preview</span>
                  </Button>

                  <Button
                    onClick={() => openEditModal(template)}
                    variant="secondary"
                    size="sm"
                    title="Edit Template"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <FiEdit size={18} />
                    <span>Edit</span>
                  </Button>

                  <Button
                    onClick={() => {
                      setEditingTemplate(template);
                      setShowTestModal(true);
                    }}
                    variant="success"
                    size="sm"
                    title="Send Test Email"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <FiSend size={18} />
                    <span>Test</span>
                  </Button>

                  <Button
                    onClick={() => handleDelete(template.template_id)}
                    variant="danger"
                    size="sm"
                    title="Delete Template"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <FiTrash2 size={18} />
                    <span>Delete</span>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl transform transition-all">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 text-white">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <FiMail size={28} />
                {editingTemplate ? 'Edit Email Template' : 'Create New Email Template'}
              </h2>
              <p className="text-blue-100 mt-1 text-sm">
                {editingTemplate ? 'Update your template details below' : 'Design a new automated email template'}
              </p>
            </div>
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-140px)]">
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Template Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.template_name}
                        onChange={(e) =>
                          setFormData({ ...formData, template_name: e.target.value })
                        }
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                        placeholder="e.g., Welcome Email"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Template Key <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.template_key}
                        onChange={(e) =>
                          setFormData({ ...formData, template_key: e.target.value })
                        }
                        className={`w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${
                          editingTemplate ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        placeholder="e.g., welcome_email"
                        disabled={!!editingTemplate}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                        <FiAlertCircle size={12} />
                        Unique identifier (lowercase, underscores only)
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                      placeholder="Welcome to {{company_name}}, {{user_name}}!"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                      <FiCode size={12} />
                      Use double curly braces for variables: <code className="bg-gray-100 px-1 rounded">{'{{variable}}'}</code>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      HTML Body <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.body_html}
                      onChange={(e) =>
                        setFormData({ ...formData, body_html: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg font-mono text-sm focus:border-blue-500 focus:outline-none transition-colors"
                      rows={12}
                      placeholder={`<html>\n  <body>\n    <h1>Hello {{user_name}}</h1>\n    <p>Your content here...</p>\n  </body>\n</html>`}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Plain Text Body (Fallback)
                    </label>
                    <textarea
                      value={formData.body_text}
                      onChange={(e) =>
                        setFormData({ ...formData, body_text: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                      rows={6}
                      placeholder="Plain text version for email clients that don't support HTML"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                      rows={2}
                      placeholder="Brief description of this template's purpose"
                    />
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) =>
                        setFormData({ ...formData, is_active: e.target.checked })
                      }
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="is_active" className="text-sm font-semibold text-gray-700 cursor-pointer flex items-center gap-2">
                      <FiCheck className={formData.is_active ? 'text-green-600' : 'text-gray-400'} />
                      Template is Active (can be used for sending emails)
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    variant="secondary"
                    size="md"
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    variant="primary"
                    size="md"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        {editingTemplate ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <FiCheck />
                        {editingTemplate ? 'Update Template' : 'Create Template'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-8 py-6 text-white">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <FiEye size={28} />
                Email Preview
              </h2>
              <p className="text-purple-100 mt-1 text-sm">
                Preview how your email will look with sample data
              </p>
            </div>
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FiMail size={16} />
                    Subject Line
                  </label>
                  <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border-l-4 border-purple-500 font-medium text-gray-800">
                    {previewData.subject}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FiCode size={16} />
                    HTML Preview
                  </label>
                  <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-4 py-2 text-xs text-gray-600 font-mono border-b">
                      Rendered HTML Content
                    </div>
                    <div
                      className="p-6 bg-white min-h-[200px]"
                      dangerouslySetInnerHTML={{ __html: previewData.body_html }}
                    />
                  </div>
                </div>
                {previewData.body_text && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <FiAlertCircle size={16} />
                      Plain Text Version (Fallback)
                    </label>
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 whitespace-pre-wrap font-mono text-sm text-gray-700">
                      {previewData.body_text}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
                <Button
                  onClick={() => setShowPreviewModal(false)}
                  variant="primary"
                  size="md"
                >
                  Close Preview
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Send Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-8 py-6 text-white">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <FiSend size={28} />
                Send Test Email
              </h2>
              <p className="text-green-100 mt-1 text-sm">
                Send a test email with sample data
              </p>
            </div>
            <div className="p-8">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Test Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <FiAlertCircle className="text-blue-600 mt-0.5" size={20} />
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">Test Email Information</p>
                      <p>A test email will be sent with sample variable data. This helps you verify the template formatting and content before using it in production.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-4 mt-8">
                <Button
                  onClick={() => {
                    setShowTestModal(false);
                    setTestEmail('');
                  }}
                  variant="secondary"
                  size="md"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleTestSend}
                  variant="success"
                  size="md"
                >
                  <FiSend />
                  Send Test Email
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplates;
